import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, AlertTriangle, Home } from "lucide-react";

/**
 * PaymentSuccess — landing page after Stripe hosted-checkout. Polls
 * /api/payments/status/{session_id} until it flips to paid.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshMe } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, error: null });
  const pollRef = useRef(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, paid: false, error: "Missing session_id" }); return; }
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const ps = r.data?.payment_status;
        if (ps === "paid") {
          setState({ loading: false, paid: true, error: null });
          if (refreshMe) await refreshMe();
          return;
        }
        if (ps === "failed" || ps === "expired") {
          setState({ loading: false, paid: false, error: `Payment ${ps}` });
          return;
        }
      } catch { /* keep polling */ }
      if (attemptsRef.current >= 20) {
        setState({ loading: false, paid: false, error: "Payment is still pending. Check your subs page in a minute." });
        return;
      }
      pollRef.current = setTimeout(tick, 2000);
    };
    tick();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="payment-success-page">
      {state.loading && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-[color:var(--sc-cyan)] animate-spin" />
          <h1 className="sc-heading text-2xl font-black mt-4">Confirming your payment…</h1>
          <p className="text-sm text-zinc-500 mt-2 sc-mono">This usually takes just a second.</p>
        </>
      )}
      {!state.loading && state.paid && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-[color:var(--sc-volt)]" />
          <h1 className="sc-heading text-3xl font-black mt-4">Payment received!</h1>
          <p className="text-sm text-zinc-400 mt-2">Your subscription is active. Sub perks are live in that streamer&apos;s chat right now.</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link to="/account/plans" className="sc-btn text-sm" data-testid="payment-success-mysubs">Manage Plans</Link>
            <Link to="/browse" className="sc-btn-ghost text-sm inline-flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Browse</Link>
          </div>
        </>
      )}
      {!state.loading && !state.paid && (
        <>
          <AlertTriangle className="w-10 h-10 mx-auto text-[color:var(--sc-pink)]" />
          <h1 className="sc-heading text-2xl font-black mt-4">Something went sideways</h1>
          <p className="text-sm text-zinc-400 mt-2 sc-mono">{state.error || "Unknown issue"}</p>
          <div className="mt-6"><Link to="/account/plans" className="sc-btn-ghost text-sm">Back to Plans</Link></div>
        </>
      )}
    </div>
  );
}
